import type {
  OverviewExportDeliveryMode,
  OverviewExportRecord,
  OverviewExportStatus,
} from "../Apis/overviewExportTypes";
import type { ExportDataSource, ExportEntityType } from "./exportApiHandlers";

export const EXPORT_PROGRESS_STORAGE_PREFIX = "carepro.export-progress.v1.";

export type ExportPresentation = "expanded" | "minimized" | "hidden";

export interface TrackedExportJob {
  exportId: string;
  entityType: ExportEntityType;
  source: ExportDataSource;
  entityLabel: string;
  columnCount?: number;
  recipientEmail?: string | null;
  deliveryMode?: OverviewExportDeliveryMode | null;
  canDownloadInApp: boolean;
  presentation: ExportPresentation;
  status: OverviewExportStatus;
  processedCount?: number;
  errorMessage?: string | null;
  completedRecord?: OverviewExportRecord | null;
  analyticsEntity?: ExportEntityType;
  notified?: boolean;
  /** Wall-clock start; used to cap zombie QUEUED/PROCESSING polls (~10 min). */
  startedAt?: number;
  /** First FAILED poll timestamp; cleared if the job returns to PROCESSING. */
  pendingFailedAt?: number;
}

function storageKey(userId: string): string {
  return `${EXPORT_PROGRESS_STORAGE_PREFIX}${userId}`;
}

function isPresentation(value: unknown): value is ExportPresentation {
  return value === "expanded" || value === "minimized" || value === "hidden";
}

function isStatus(value: unknown): value is OverviewExportStatus {
  return (
    value === "QUEUED" ||
    value === "PROCESSING" ||
    value === "COMPLETED" ||
    value === "FAILED"
  );
}

function isTrackedExportJob(value: unknown): value is TrackedExportJob {
  if (typeof value !== "object" || value === null) return false;
  const job = value as TrackedExportJob;
  return (
    typeof job.exportId === "string" &&
    typeof job.entityType === "string" &&
    typeof job.source === "string" &&
    typeof job.entityLabel === "string" &&
    typeof job.canDownloadInApp === "boolean" &&
    isPresentation(job.presentation) &&
    isStatus(job.status)
  );
}

/**
 * CAR-2738: sessionStorage may only keep the export id and UI state.
 * Never write presigned URLs, expiry, the status payload, or recipient email.
 */
export function sanitizeJobForStorage(job: TrackedExportJob): TrackedExportJob {
  const safe: TrackedExportJob = { ...job };
  delete safe.completedRecord;
  delete safe.recipientEmail;
  return safe;
}

export function loadExportProgress(userId: string): TrackedExportJob[] {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isTrackedExportJob).map(sanitizeJobForStorage);
  } catch {
    return [];
  }
}

export function saveExportProgress(
  userId: string,
  jobs: TrackedExportJob[]
): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    const safe = jobs.map(sanitizeJobForStorage);
    window.sessionStorage.setItem(storageKey(userId), JSON.stringify(safe));
  } catch {
    // private mode / quota — progress UI still works in memory
  }
}

export function clearExportProgress(userId?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (userId) {
      window.sessionStorage.removeItem(storageKey(userId));
      return;
    }
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(EXPORT_PROGRESS_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function isInFlightExportStatus(status: OverviewExportStatus): boolean {
  return status === "QUEUED" || status === "PROCESSING";
}

export function jobsToPersist(jobs: TrackedExportJob[]): TrackedExportJob[] {
  return jobs
    .filter((job) => {
      if (isInFlightExportStatus(job.status)) return true;
      if (job.presentation === "expanded") return true;
      return !job.notified;
    })
    .map(sanitizeJobForStorage);
}
